import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DecisionAction, HomeIaActionInputProperty } from "../../../models/home";
import { HomeDecisionDetailI18nService } from "../../../services/home-ia/home-decision-detail-i18n.service";
import { LanguageService } from "../../../services/language/language.service";

interface DecisionActionFieldEntry {
  key: string;
  inputId: string;
  field: HomeIaActionInputProperty;
}

@Component({
  selector: "app-home-decision-action-form",
  templateUrl: "./home-decision-action-form.component.html",
  styleUrls: ["./home-decision-action-form.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeDecisionActionFormComponent implements OnChanges {
  @Input() action: DecisionAction | null = null;
  @Input() loading = false;
  @Output() submitRequest = new EventEmitter<Record<string, unknown>>();

  readonly form = new FormGroup({});
  fieldEntries: readonly DecisionActionFieldEntry[] = [];
  readonly confirmationControlName = "confirmExecution";

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    public readonly lang: LanguageService,
    @Inject(HomeDecisionDetailI18nService) public readonly detailI18n: HomeDecisionDetailI18nService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["action"]) {
      this.rebuildForm();
    }
  }

  get hasFields(): boolean {
    return this.fieldEntries.length > 0 || this.requiresConfirmation;
  }

  get formIntro(): string | null {
    return this.detailI18n.translateActionFormCopy(this.action);
  }

  get requiresConfirmation(): boolean {
    return !!this.action?.confirmationRequired;
  }

  get confirmationLabel(): string {
    return this.detailI18n.translateActionConfirmationLabel(this.action);
  }

  get confirmationControlId(): string {
    return `${this.action?.actionKey ?? "action"}-confirm`;
  }

  focusFirstField(): void {
    queueMicrotask(() => {
      this.host.nativeElement.querySelector<HTMLElement>("input, textarea, select, button")?.focus();
    });
  }

  trackByFieldKey(_: number, entry: DecisionActionFieldEntry): string {
    return entry.key;
  }

  isBooleanField(field: HomeIaActionInputProperty): boolean {
    return field.type === "boolean";
  }

  isTextareaField(field: HomeIaActionInputProperty): boolean {
    return field.type === "textarea";
  }

  isSelectField(field: HomeIaActionInputProperty): boolean {
    return field.type === "select";
  }

  isStandardInputField(field: HomeIaActionInputProperty): boolean {
    return !this.isBooleanField(field) && !this.isTextareaField(field) && !this.isSelectField(field);
  }

  resolveInputType(field: HomeIaActionInputProperty): string {
    switch (field.type) {
      case "number":
        return "number";
      case "date":
        return "date";
      case "datetime":
        return "datetime-local";
      default:
        return "text";
    }
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstField();
      return;
    }

    const rawValue = this.form.getRawValue() as Record<string, unknown>;
    this.submitRequest.emit(rawValue);
  }

  onCancel(): void {
    this.rebuildForm();
  }

  private rebuildForm(): void {
    Object.keys(this.form.controls).forEach((controlName) => this.form.removeControl(controlName));

    const schema = this.action?.inputSchema;
    const requiredKeys = new Set(schema?.required ?? []);
    this.fieldEntries = Object.entries(schema?.properties ?? {}).map(([key, field]) => {
      const initialValue = field.type === "boolean" ? false : "";
      const validators = requiredKeys.has(key) || field.required ? [Validators.required] : [];
      this.form.addControl(key, new FormControl(initialValue, validators));

      return {
        key,
        inputId: `${this.action?.actionKey ?? "action"}-${key}`,
        field
      };
    });

    if (this.requiresConfirmation) {
      this.form.addControl(this.confirmationControlName, new FormControl(false, Validators.requiredTrue));
    }
  }
}